import React, { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'));
  }

  if (window.turnstile?.render) {
    return Promise.resolve(window.turnstile);
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const resolveWhenReady = () => {
      if (window.turnstile?.render) {
        resolve(window.turnstile);
        return;
      }
      if (window.turnstile?.ready) {
        window.turnstile.ready(() => resolve(window.turnstile));
        return;
      }
      reject(new Error('Turnstile script loaded without a renderer'));
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', resolveWhenReady, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolveWhenReady;
    script.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

const TurnstileWidget = ({
  siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY,
  resetKey = 0,
  onVerify,
  onExpire,
  onError,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState(siteKey ? 'loading' : 'disabled');

  useEffect(() => {
    if (!siteKey) {
      setStatus('disabled');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;

        if (widgetIdRef.current && typeof turnstile.remove === 'function') {
          turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
        containerRef.current.innerHTML = '';

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'public_signup',
          theme: 'light',
          size: 'flexible',
          appearance: 'always',
          callback: (token) => {
            setStatus('verified');
            onVerify?.(token);
          },
          'expired-callback': () => {
            setStatus('expired');
            onExpire?.();
          },
          'timeout-callback': () => {
            setStatus('expired');
            onExpire?.();
          },
          'error-callback': (errorCode) => {
            setStatus('error');
            onError?.(errorCode);
            return true;
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setStatus('error');
        onError?.(error);
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile?.remove) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      } catch (_) {}
    };
  }, [onError, onExpire, onVerify, resetKey, siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className={`turnstile-widget turnstile-widget--${status}`} aria-live="polite">
      <div ref={containerRef} className="turnstile-widget-frame" />
    </div>
  );
};

export default TurnstileWidget;
